import { CustomEventWithDetail, html, View } from 'rune-ts';
import { ButtonFilter } from '../../atoms/ButtonFilter/ButtonFilter';
import klass from './MultiSelectButtonGroup.module.scss';
import { differenceBy, filter, pipe, pluck, toArray } from '@fxts/core';

export interface SelectableButtonData<ID extends string | number> {
  id: ID;

  name: string;

  selected: boolean;
}

class SelectableButton<ID extends string | number> extends View<SelectableButtonData<ID>> {
  override template() {
    const { name, selected } = this.data;
    return html`
      <div class="${klass.btn_wrapper}">
        ${new ButtonFilter({ label: name, klass: `${klass.btn}`, is_active: selected })}
      </div>
    `;
  }

  public toggle() {
    const button = this.subView(ButtonFilter);
    button?.toggle();
  }

  public setStatus(is_active: boolean) {
    const button = this.subView(ButtonFilter);
    button?.setStatus(is_active);
  }
}

class AbstractMultiSelectButtonGroupChangeEvent<ID extends string | number> extends CustomEventWithDetail<{
  selected: SelectableButtonData<ID>[];
}> {}

class AbstractMultiSelectButtonGroupClickEvent<ID extends string | number> extends CustomEventWithDetail<{
  selected: SelectableButtonData<ID>[];
}> {}

abstract class AbstractMultiSelectButtonGroup<ID extends string | number> extends View<
  SelectableButtonData<ID>[]
> {
  protected readonly ChildButton: typeof SelectableButton<ID>;

  protected container_klass = '';

  protected datas: SelectableButtonData<ID>[];

  protected constructor(datas: Readonly<SelectableButtonData<ID>>[]) {
    super(structuredClone(datas));
    this.datas = structuredClone(datas);
    this.ChildButton = SelectableButton<ID>;
  }

  override template() {
    return html`<div class="${this.container_klass}">
      ${this.datas.map((data) => new this.ChildButton(data))}
    </div>`;
  }

  /**
   * 초기 데이터에서 selected data가 변경되었는지 확인하는 함수입니다.
   */
  public hasChanged(): boolean {
    const initial_selected_datas = pipe(
      this.data,
      filter((data) => data.selected),
      toArray,
    );
    const current_selected_datas = this.getSelected();

    const diff1 = toArray(differenceBy((a) => a.id, initial_selected_datas, current_selected_datas));
    const diff2 = toArray(differenceBy((a) => a.id, current_selected_datas, initial_selected_datas));

    return diff1.length > 0 || diff2.length > 0;
  }

  public getSelected() {
    return pipe(
      this.datas,
      filter((data) => data.selected),
      toArray,
    );
  }

  public getSelectedIds() {
    return pipe(this.getSelected(), pluck('id'), toArray);
  }

  protected setButtonStatus(id: ID, is_active: boolean) {
    this.subViews(SelectableButton)
      ?.find((button) => button.data.id === id)
      ?.setStatus(is_active);
    this.onSetStatus(id, is_active);
  }

  public toggleButton(id: ID) {
    this.subViews(SelectableButton)
      ?.find((button) => button.data.id === id)
      ?.toggle();
    this.onToggle(id);
  }

  public isSelected(id: ID): boolean {
    const ids = toArray(pluck('id', this.data));
    return ids.includes(id);
  }

  private getData(id: ID) {
    return this.datas.find((data) => data.id === id);
  }

  protected onToggle(id: ID): void {
    const toggled_data = this.getData(id);
    if (toggled_data) {
      toggled_data.selected = !toggled_data.selected;
    }
  }

  protected onSetStatus(id: ID, is_active: boolean): void {
    const toggled_data = this.getData(id);
    if (toggled_data) {
      toggled_data.selected = is_active;
    }
  }

  override onRender() {
    this.delegate('click', this.ChildButton, (_, view) => {
      this.onToggle(view.data.id);
      if (this.hasChanged()) {
        this.dispatchEvent(AbstractMultiSelectButtonGroupChangeEvent, {
          bubbles: true,
          detail: {
            selected: this.datas,
          },
        });
      }
    });

    this.delegate('click', this.ChildButton, () => {
      this.dispatchEvent(AbstractMultiSelectButtonGroupClickEvent, {
        bubbles: true,
        detail: {
          selected: this.datas,
        },
      });
    });
  }
}
export class MultiSelectButtonGroup<
  ID extends string | number = string,
> extends AbstractMultiSelectButtonGroup<ID> {
  constructor(
    datas: SelectableButtonData<ID>[],
    private options?: { klass?: string },
  ) {
    super(datas);
    this.container_klass = `${klass.btn_container} ${klass.gap8} ${this.options?.klass}`;
  }

  override onRender() {
    super.onRender();
    this.addEventListener(AbstractMultiSelectButtonGroupClickEvent, () => {
      this.dispatchEvent(MultiSelectButtonGroupClickEvent<ID>, {
        bubbles: true,
        detail: {
          selected: this.getSelected(),
        },
      });
    });
  }
}

export class AllMultiSelectButtonGroupChangeEvent<
  ID extends string | number = string,
> extends AbstractMultiSelectButtonGroupChangeEvent<ID> {}

export class MultiSelectButtonGroupClickEvent<
  ID extends string | number = string,
> extends AbstractMultiSelectButtonGroupChangeEvent<ID> {}

export class AllMultiSelectButtonGroup<
  ID extends string | number = string,
> extends AbstractMultiSelectButtonGroup<ID> {
  constructor(
    datas: SelectableButtonData<ID>[],
    private option: { all: { id: ID; selected: boolean; name: string } },
  ) {
    super([option.all, ...datas]);
    this.container_klass = `${klass.btn_container} ${klass.gap8}`;
  }

  override onRender() {
    this.delegate('click', this.ChildButton, (_, view) => {
      if (view.data.id === this.option.all.id) {
        this.clickAllButton();
      } else {
        this.clickButton(view.data.id);
      }

      if (this.hasChanged()) {
        this.dispatchEvent(AllMultiSelectButtonGroupChangeEvent<ID>, {
          bubbles: true,
          detail: {
            selected: this.getSelected(),
          },
        });
      }
    });
  }

  private clickButton(id: ID) {
    this.onToggle(id);
    this.setButtonStatus(this.option.all.id, this.getSelected().length === 0);
  }

  private clickAllButton() {
    this.getSelected().forEach((selected) => this.setButtonStatus(selected.id, false));
    this.setButtonStatus(this.option.all.id, true);
  }

  override getSelected() {
    return pipe(
      this.datas,
      filter((data) => data.selected && data.id !== this.option.all.id),
      toArray,
    );
  }
}
